// frontend/src/pages/ConsultaEstoque.js
import React, { useState } from 'react';
import { 
    Search, Download, Box, ArrowLeft, History, FileText, 
    TrendingUp, TrendingDown, Loader2, AlertCircle, MapPin, X, Package
} from 'lucide-react';
import useApi from '../hooks/useApi'; 

// ============================================================================
// MODAL: VISUALIZAR NOTA FISCAL
// ============================================================================
const VisualizarNotaModal = ({ invoice, onClose, formatCurrency }) => {
    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-gray-100 animate-in fade-in duration-200">
            <div className="flex items-center justify-between p-4 border-b bg-white shadow-sm shrink-0">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FileText className="text-blue-600" /> Detalhes do Lançamento: NF-e nº {invoice.invoice_number === 'S/N' ? 'Entrada Manual' : invoice.invoice_number}
                </h2>
                <button 
                    onClick={onClose} 
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition-colors"
                >
                    <X size={20} /> Fechar
                </button>
            </div>
            
            <div className="flex-1 p-6 md:p-8 overflow-y-auto w-full">
                <div className="w-full mx-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Fornecedor</p>
                            <p className="font-bold text-lg text-gray-900">{invoice.supplier_name}</p>
                            <p className="text-sm text-gray-600">CNPJ: {invoice.supplier_cnpj || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Documento</p>
                            <p className="font-bold text-lg text-gray-900">NF-e nº {invoice.invoice_number}</p>
                            <p className="text-sm text-gray-600">Série: {invoice.series || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Datas</p>
                            <p className="font-bold text-gray-900">Emissão: {invoice.issue_date ? invoice.issue_date.split('-').reverse().join('/') : '-'}</p>
                            <p className="text-sm text-gray-600">Lançamento: {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                        </div>
                        <div className="md:col-span-2">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Depósito Destino</p>
                            <p className="font-bold text-lg text-gray-900">{invoice.deposit_name || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Valor Total</p>
                            <p className="text-3xl font-black text-blue-700">{formatCurrency(invoice.total_amount)}</p>
                        </div>
                    </div>

                    {invoice.observations && (
                        <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200 shadow-sm">
                            <p className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-2">Observações Adicionais</p>
                            <p className="text-base text-yellow-900">{invoice.observations}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                            <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                                <Package className="text-gray-600" size={20}/> Itens Lançados no Estoque
                            </h3>
                            <span className="text-sm font-bold text-gray-500 bg-gray-200 px-3 py-1 rounded-full">{invoice.items?.length || 0} Itens Registrados</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left whitespace-nowrap">
                                <thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs tracking-wider border-b">
                                    <tr>
                                        <th className="px-6 py-4">Produto Vinculado (Sistema)</th>
                                        <th className="px-6 py-4">Cód. Fornecedor</th>
                                        <th className="px-6 py-4 text-right">Qtd. na NF</th>
                                        <th className="px-6 py-4 text-center">Fator Conv.</th>
                                        <th className="px-6 py-4 text-right bg-blue-50/50">Qtd. Estoque</th>
                                        <th className="px-6 py-4 text-right">Custo Unitário</th>
                                        <th className="px-6 py-4 text-right bg-gray-50">Custo Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {invoice.items && invoice.items.length > 0 ? (
                                        invoice.items.map((item, index) => (
                                            <tr key={index} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-6 py-4 font-medium text-gray-900">{item.product_name}</td>
                                                <td className="px-6 py-4 text-gray-500 text-xs">{item.supplier_product_code}</td>
                                                <td className="px-6 py-4 text-right text-gray-600 font-medium">{item.quantity_billed}</td>
                                                <td className="px-6 py-4 text-center text-gray-500 text-xs">x {item.conversion_factor}</td>
                                                <td className="px-6 py-4 text-right font-bold text-blue-600 bg-blue-50/30">{item.quantity_received}</td>
                                                <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(item.unit_cost)}</td>
                                                <td className="px-6 py-4 text-right font-bold text-gray-800 bg-gray-50/50">{formatCurrency(item.total_price)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                                                Nenhum item encontrado nesta nota fiscal.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// COMPONENTE PRINCIPAL: CONSULTA DE ESTOQUE
// ============================================================================
const ConsultaEstoque = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // --- VERIFICAÇÃO DE PERMISSÕES GRANULARES ---
    const isSuper = currentUser?.is_super_user;
    const perms = currentUser?.permissions || {};
    const canExport = isSuper || perms.inventory_consult_create || perms.inventory_consult_edit;

    // --- ESTADOS ---
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [activeTab, setActiveTab] = useState('historico'); 
    
    // Filtro por Depósito
    const [selectedDepositFilter, setSelectedDepositFilter] = useState('geral');

    // Novo Estado para o Modal de NF
    const [viewingInvoice, setViewingInvoice] = useState(null);

    // --- HELPERS ---
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    // --- FUNÇÕES DE BUSCA E DADOS ---
    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!searchQuery.trim()) return;

        setIsSearching(true);
        setHasSearched(true);
        setSelectedProduct(null);
        setSelectedDepositFilter('geral');

        try {
            const results = await apiFetch(`/stock/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchResults(results || []);
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
            setSearchResults([]); 
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectProduct = async (product) => {
        setLoadingDetails(true);
        setSelectedDepositFilter('geral'); // Reseta o filtro ao trocar de produto
        try {
            const details = await apiFetch(`/stock/product/${product.id}`);
            setSelectedProduct(details);
            setActiveTab('historico');
        } catch (error) {
            console.error("Erro ao carregar detalhes do produto:", error);
            setSelectedProduct({
                id: product.id || 1,
                codigo: product.code || 'PRD001',
                ean: product.ean || '7891020304050',
                descricao: product.name || 'Produto Não Encontrado',
                valor_custo: 0,
                valor_venda: 0,
                estoque_total: 0,
                depositos: [],
                historico: [],
                nfs_recebidas: []
            });
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleOpenInvoice = async (invoiceId) => {
        if (!invoiceId) return;
        try {
            const data = await apiFetch(`/invoice-entry/${invoiceId}`);
            setViewingInvoice(data);
        } catch (err) {
            alert("Erro ao carregar detalhes da nota. Pode ter sido excluída.");
        }
    };

    // --- EXPORTAÇÃO PARA EXCEL ---
    const handleExportExcel = async () => {
        if (!canExport) return;

        setIsExporting(true);
        try {
            const exportData = await apiFetch(`/stock/export`);
            
            if (!exportData || exportData.length === 0) {
                alert("Nenhum dado encontrado para exportação.");
                setIsExporting(false);
                return;
            }

            const delimiter = ';'; 
            
            const headers = [
                'DESCRIÇÃO', 
                'DEPÓSITO', 
                'QUANTIDADE', 
                'CUSTO UNITARIO', 
                'CUSTO TOTAL', 
                'VALOR DE VENDA UNITARIO', 
                'VALOR DE VENDA TOTAL'
            ];
            
            const csvRows = [headers.join(delimiter)];

            // FUNÇÃO ATUALIZADA: Retorna apenas o número, com vírgula nas casas decimais
            const formatNumberExcel = (value) => {
                return Number(value || 0).toFixed(2).replace('.', ','); 
            };

            const formatQtyExcel = (value) => {
                return String(value || 0).replace('.', ',');
            };

            exportData.forEach(item => {
                const custoUnitario = parseFloat(item.valor_custo) || 0;
                const vendaUnitario = parseFloat(item.valor_venda) || 0;
                const qtd = parseFloat(item.quantidade) || 0;
                
                const custoTotal = custoUnitario * qtd;
                const vendaTotal = vendaUnitario * qtd;

                const row = [
                    `"${item.descricao || ''}"`,
                    `"${item.deposito || ''}"`,
                    formatQtyExcel(qtd),
                    formatNumberExcel(custoUnitario),
                    formatNumberExcel(custoTotal),
                    formatNumberExcel(vendaUnitario),
                    formatNumberExcel(vendaTotal)
                ];
                csvRows.push(row.join(delimiter));
            });

            const csvString = csvRows.join('\n');
            const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' }); 
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `Consulta_Estoque_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error("Erro ao exportar:", error);
            alert("Não foi possível gerar a exportação. Verifique a conexão com o servidor.");
        } finally {
            setIsExporting(false);
        }
    };

    // --- FILTROS E CÁLCULOS INTELIGENTES DO HISTÓRICO ---
    
    // Calcula o saldo global progressivo apenas para a visão "Geral"
    let runningGlobalTotal = parseFloat(selectedProduct?.estoque_total) || 0;

    const filteredHistorico = selectedProduct?.historico 
        ? (selectedDepositFilter === 'geral' 
            ? selectedProduct.historico.map(mov => {
                // Salva o total atual para exibir nesta linha
                const displayBalance = runningGlobalTotal;
                
                // Ajusta o total global para a PRÓXIMA linha (mais antiga)
                // Se foi entrada, a gente subtrai pra voltar no tempo. Se foi saída, a gente soma.
                const qtd = parseFloat(mov.quantidade) || 0;
                if (mov.tipo === 'entrada') {
                    runningGlobalTotal -= qtd;
                } else {
                    runningGlobalTotal += qtd;
                }
                
                return { ...mov, display_balance: displayBalance };
            })
            : selectedProduct.historico.filter(h => h.deposit_id === selectedDepositFilter).map(mov => ({
                ...mov,
                display_balance: mov.estoque_atualizado // Se for depósito específico, usa o saldo exato do banco
            })))
        : [];
        
    const filteredNfs = selectedProduct?.nfs_recebidas
        ? (selectedDepositFilter === 'geral'
            ? selectedProduct.nfs_recebidas
            : selectedProduct.nfs_recebidas.filter(n => n.deposit_id === selectedDepositFilter))
        : [];

    return (
        <div className="p-6 max-w-7xl mx-auto animate-in fade-in duration-300 relative">
            
            {/* MODAL VISUALIZAR NOTA */}
            {viewingInvoice && (
                <VisualizarNotaModal 
                    invoice={viewingInvoice} 
                    onClose={() => setViewingInvoice(null)} 
                    formatCurrency={formatCurrency}
                />
            )}

            {/* CABEÇALHO E BARRA DE BUSCA */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Box className="text-blue-600" /> Consulta de Estoque
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Busque produtos por descrição, código ou EAN.</p>
                </div>

                <div className="flex flex-col sm:flex-row w-full lg:w-auto items-stretch sm:items-center gap-3">
                    <form onSubmit={handleSearch} className="flex gap-2 flex-1 lg:w-96">
                        <div className="relative flex-1">
                            <input 
                                type="text" 
                                placeholder="Buscar produto..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm h-full"
                            />
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        </div>
                        <button 
                            type="submit" 
                            className="bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-lg shadow-sm transition-colors flex items-center justify-center shrink-0"
                            title="Pesquisar"
                        >
                            <Search size={20} />
                        </button>
                    </form>
                    
                    {canExport && (
                        <button 
                            onClick={handleExportExcel}
                            disabled={isExporting}
                            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium shadow-sm transition-colors disabled:bg-green-400 whitespace-nowrap"
                        >
                            {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                            <span className="hidden sm:inline">Exportar Excel</span>
                        </button>
                    )}
                </div>
            </div>

            {/* ÁREA DE CONTEÚDO PRINCIPAL */}
            
            {/* 1. TELA INICIAL VAZIA */}
            {!hasSearched && !selectedProduct && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex flex-col items-center justify-center text-center h-[50vh]">
                    <div className="bg-blue-50 p-4 rounded-full mb-4">
                        <Search size={48} className="text-blue-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Pronto para pesquisar</h2>
                    <p className="text-gray-500 max-w-md">
                        Digite o nome, código ou EAN na barra acima e clique na lupa para encontrar um produto, ou clique em exportar para baixar a listagem completa.
                    </p>
                </div>
            )}

            {/* 2. RESULTADOS DA BUSCA (LISTA) */}
            {hasSearched && !selectedProduct && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                        <h3 className="font-bold text-gray-700">Resultados para: "{searchQuery}"</h3>
                        <span className="text-sm text-gray-500">{searchResults.length} produto(s) encontrado(s)</span>
                    </div>
                    
                    {isSearching ? (
                        <div className="p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                    ) : searchResults.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <AlertCircle size={32} className="text-gray-400 mb-2" />
                            <p>Nenhum produto encontrado com este termo.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-3">Código / EAN</th>
                                        <th className="px-6 py-3">Descrição</th>
                                        <th className="px-6 py-3 text-right">Estoque Total</th>
                                        <th className="px-6 py-3 text-center">Ação</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {searchResults.map((item) => (
                                        <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">{item.code || '-'}</div>
                                                <div className="text-xs text-gray-500">{item.ean || '-'}</div>
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-800">{item.name}</td>
                                            <td className="px-6 py-4 text-right font-bold text-blue-700">{item.total_stock || 0}</td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => handleSelectProduct(item)}
                                                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-blue-600 font-semibold text-xs transition-colors"
                                                >
                                                    Ver Detalhes
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* 3. VISÃO DETALHADA DO PRODUTO */}
            {selectedProduct && (
                <div className="animate-in slide-in-from-bottom-4 duration-300">
                    
                    {/* Botão Voltar */}
                    <button 
                        onClick={() => setSelectedProduct(null)} 
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-4 font-medium transition-colors"
                    >
                        <ArrowLeft size={18} /> Voltar para resultados
                    </button>

                    {loadingDetails ? (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                    ) : (
                        <div className="space-y-6">
                            
                            {/* HEADER DO PRODUTO (Cards Superiores) */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="mb-6 pb-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">{selectedProduct.descricao}</h2>
                                        <div className="flex gap-4 mt-2 text-sm text-gray-500 font-mono">
                                            <span>COD: {selectedProduct.codigo || '-'}</span>
                                            <span>EAN: {selectedProduct.ean || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                    {/* Resumo de Valores */}
                                    <div className="lg:col-span-1 flex flex-col gap-4 border-r border-gray-100 pr-6">
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                            <span className="text-xs font-bold text-gray-500 uppercase">Custo Atual</span>
                                            <div className="text-lg font-bold text-gray-800 mt-1">{formatCurrency(selectedProduct.valor_custo)}</div>
                                        </div>
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                            <span className="text-xs font-bold text-blue-600 uppercase">Valor de Venda</span>
                                            <div className="text-lg font-bold text-blue-800 mt-1">{formatCurrency(selectedProduct.valor_venda)}</div>
                                        </div>
                                    </div>

                                    {/* Estoque por Depósito (Agora com Filtro Clicável) */}
                                    <div className="lg:col-span-3">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-bold text-gray-700 uppercase flex items-center gap-2">
                                                <MapPin size={16} /> Estoque por Depósito (Clique para filtrar o histórico)
                                            </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {/* Card Geral (Todos) */}
                                            <div 
                                                onClick={() => setSelectedDepositFilter('geral')}
                                                className={`border rounded-lg p-3 flex justify-between items-center cursor-pointer transition-all shadow-sm ${selectedDepositFilter === 'geral' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                                title="Mostrar movimentações de todos os depósitos"
                                            >
                                                <span className="text-sm font-bold text-gray-800 truncate pr-2">Geral (Todos)</span>
                                                <span className="font-black text-blue-700 text-lg">{selectedProduct.estoque_total}</span>
                                            </div>

                                            {/* Cards Dinâmicos */}
                                            {selectedProduct.depositos && selectedProduct.depositos.map(dep => (
                                                <div 
                                                    key={dep.id} 
                                                    onClick={() => setSelectedDepositFilter(dep.id)}
                                                    className={`border rounded-lg p-3 flex justify-between items-center cursor-pointer transition-all shadow-sm ${selectedDepositFilter === dep.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                                    title={`Filtrar movimentações pelo depósito: ${dep.nome}`}
                                                >
                                                    <span className={`text-sm font-medium truncate pr-2 ${selectedDepositFilter === dep.id ? 'text-blue-800' : 'text-gray-700'}`}>{dep.nome}</span>
                                                    <span className={`font-bold text-lg ${selectedDepositFilter === dep.id ? 'text-blue-900' : 'text-gray-900'}`}>{dep.quantidade}</span>
                                                </div>
                                            ))}
                                            
                                            {(!selectedProduct.depositos || selectedProduct.depositos.length === 0) && (
                                                <div className="text-sm text-gray-500 italic col-span-3 p-2">Nenhum estoque registrado nos depósitos.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ABAS (HISTÓRICO E NFs) COM FILTRO DE DEPÓSITO ATIVO */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="flex border-b border-gray-200 bg-gray-50 px-4 pt-2 gap-2">
                                    <button 
                                        onClick={() => setActiveTab('historico')}
                                        className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'historico' ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                                    >
                                        <History size={16} /> Histórico do Produto (Kardex)
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('nfs')}
                                        className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'nfs' ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                                    >
                                        <FileText size={16} /> NFs Recebidas
                                    </button>
                                </div>

                                <div className="p-0">
                                    {/* CONTEÚDO: HISTÓRICO */}
                                    {activeTab === 'historico' && (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-white border-b text-gray-600 font-semibold uppercase text-[11px] sticky top-0">
                                                    <tr>
                                                        <th className="px-6 py-4">Data</th>
                                                        <th className="px-6 py-4">Tipo</th>
                                                        <th className="px-6 py-4">Depósito</th>
                                                        <th className="px-6 py-4 text-center">Qtd. Movimentada</th>
                                                        <th className="px-6 py-4">Histórico / Documento</th>
                                                        <th className="px-6 py-4 text-right">Estoque Após Mov.</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {filteredHistorico.length > 0 ? (
                                                        filteredHistorico.map(mov => (
                                                            <tr key={mov.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-6 py-3 text-gray-600 whitespace-nowrap">{formatDate(mov.data)}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap">
                                                                    {mov.tipo === 'entrada' ? (
                                                                        <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-full w-fit text-xs font-bold border border-green-100"><TrendingUp size={12} /> ENTRADA</span>
                                                                    ) : (
                                                                        <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded-full w-fit text-xs font-bold border border-red-100"><TrendingDown size={12} /> SAÍDA</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-3 text-gray-800 font-medium">{mov.deposito || '-'}</td>
                                                                <td className={`px-6 py-3 text-center font-bold ${mov.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {mov.tipo === 'entrada' ? '+' : '-'}{mov.quantidade}
                                                                </td>
                                                                <td className="px-6 py-3 font-medium text-gray-800">{mov.motivo}</td>
                                                                <td className="px-6 py-3 text-right font-bold text-gray-900">{mov.display_balance}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500 italic">Nenhum histórico de movimentação encontrado para este filtro.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* CONTEÚDO: NFS RECEBIDAS */}
                                    {activeTab === 'nfs' && (
                                        <div className="overflow-x-auto bg-gray-50/30">
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-white border-b text-gray-600 font-semibold uppercase text-[11px] sticky top-0">
                                                    <tr>
                                                        <th className="px-6 py-4">Data Recebimento</th>
                                                        <th className="px-6 py-4 font-bold text-gray-800">Nº da Nota Fiscal</th>
                                                        <th className="px-6 py-4 text-center">Qtd. Recebida</th>
                                                        <th className="px-6 py-4 text-right">Custo Unitário</th>
                                                        <th className="px-6 py-4 text-right">Custo Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 bg-white">
                                                    {filteredNfs.length > 0 ? (
                                                        filteredNfs.map(nf => (
                                                            <tr key={nf.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{formatDate(nf.data_recebimento).split(' ')[0]}</td>
                                                                <td className="px-6 py-4 font-bold text-gray-900">
                                                                    <button 
                                                                        onClick={() => handleOpenInvoice(nf.invoice_id)}
                                                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
                                                                        title="Abrir detalhes desta Nota Fiscal"
                                                                    >
                                                                        <FileText size={16} className="text-blue-500" />
                                                                        {nf.numero_nf === 'S/N' ? 'Entrada Manual' : nf.numero_nf}
                                                                    </button>
                                                                </td>
                                                                <td className="px-6 py-4 text-center font-bold text-blue-600 bg-blue-50/30">{nf.quantidade}</td>
                                                                <td className="px-6 py-4 text-right font-medium text-gray-700">{formatCurrency(nf.custo_unitario)}</td>
                                                                <td className="px-6 py-4 text-right font-bold text-gray-900 bg-gray-50/50">{formatCurrency(nf.quantidade * nf.custo_unitario)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 italic">Nenhuma nota fiscal registrada para este produto no depósito selecionado.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConsultaEstoque;